import { useApiQuery } from "app/modules/api-client";
import type { Data } from "app/modules/api-client/queryTypes";
import { useDisplayAdvertiserPublisherDataAndParams } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useDisplayAdvertiserPublisherDataAndParams";
import DurationService, { BasicDurations } from "app/services/DurationService";

import type { ITopIndustryRow } from "./getTopIndustriesColumns";

const METHOD_NAME = "adIntelligencePublisherTopIndustriesList";

export type ApiResponse = Data<"AdIntelligenceMediaPartners", typeof METHOD_NAME>;
export type ITopIndustryCompareRow = ApiResponse[number];

const formatCategory = (category?: string | null) => category?.replace(/_/g, " ").replace("~", " > ") ?? "";

const selectSingleDomain = (response: ApiResponse, domain: string): ITopIndustryRow[] =>
    response.map((item) => ({
        category: formatCategory(item.category),
        subCategory: formatCategory(item.subCategory),
        spend: item.spend[domain],
        spendShare: item.spendShare[domain],
        impressions: item.impressions[domain],
        impressionsShare: item.impressionsShare[domain],
        visits: item.visits[domain],
        visitsShare: item.visitsShare[domain],
    }));

const selectCompareDomains = (response: ApiResponse): ITopIndustryCompareRow[] =>
    response.map((item) => ({
        ...item,
        category: formatCategory(item.category),
        subCategory: formatCategory(item.subCategory),
    }));

export const useTopIndustries = () => {
    const params = useDisplayAdvertiserPublisherDataAndParams();
    const { from, to, isWindow } = DurationService.getDurationData(params.duration ?? "").forAPI;
    const domains = (params.key ?? "").split(",");
    const isCompareMode = domains.length > 1;

    const { data, isLoading, isFetching, isError } = useApiQuery<
        "AdIntelligenceMediaPartners",
        typeof METHOD_NAME,
        ITopIndustryRow[] | ITopIndustryCompareRow[]
    >(
        {
            tag: "AdIntelligenceMediaPartners",
            methodName: METHOD_NAME,
            options: {
                select: (response) =>
                    isCompareMode ? selectCompareDomains(response) : selectSingleDomain(response, domains[0]),
            },
        },
        {
            domains,
            country: params.country,
            from,
            to,
            isWindow,
            ...(isWindow ? { latest: BasicDurations.LAST_TWENTY_EIGHT_DAYS } : {}),
        },
    );

    return {
        data: !isError && data ? data : undefined,
        isLoading: isLoading || isFetching,
        isError,
        isCompareMode,
        domains,
    };
};
